'use client'

import { useState, useEffect, useRef } from 'react'

interface LazyBackgroundDivProps {
  src: string
  children: React.ReactNode
  className?: string
  style?: React.CSSProperties
}

export default function LazyBackgroundDiv({
  src,
  children,
  className = '',
  style = {},
}: LazyBackgroundDivProps) {
  const [isIntersecting, setIsIntersecting] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsIntersecting(true)
          observer.disconnect()
        }
      },
      { rootMargin: '200px' } // Load well before visible
    )

    if (ref.current) {
      observer.observe(ref.current)
    }

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    if (isIntersecting) {
      // Preload image for better caching
      const img = new Image()
      img.src = src
    }
  }, [isIntersecting, src])

  // Always show the background image to maintain visual consistency
  const backgroundStyle = {
    ...style,
    backgroundImage: `url('${src}')`,
  }

  return (
    <div ref={ref} className={className} style={backgroundStyle}>
      {children}
    </div>
  )
}
